import React, { useContext, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Download,
  FileVideo,
  Loader2,
  RefreshCw,
  Video,
  X,
} from "lucide-react";
import { UrlContext } from "../../../contexts/UrlContext";

export default function MeetingVideo({
  projectID,
  expertID,
  meetingID: providedMeetingID,
  setIsViewVideoDetails,
  onClose,
  clickingOnProfile,
}) {
  const { API_URL } = useContext(UrlContext);

  const [loading, setLoading] = useState(false);
  const [loadingVideo, setLoadingVideo] = useState(false);
  const [meetingID, setMeetingID] = useState(providedMeetingID || null);
  const [recordingUrl, setRecordingUrl] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const closeModal = () => {
    if (onClose) {
      onClose();
      return;
    }

    if (setIsViewVideoDetails) {
      setIsViewVideoDetails(false);
    }
  };

  const videoStreamUrl = useMemo(() => {
    return recordingUrl
      ? `${API_URL}stream-recorded-meeting-video?videoURL=${encodeURIComponent(
          recordingUrl
        )}`
      : "";
  }, [API_URL, recordingUrl]);

  useEffect(() => {
    document.body.classList.add("overflow-hidden");

    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, []);

  const getMeetingID = async () => {
    if (providedMeetingID) {
      setMeetingID(providedMeetingID);
      return providedMeetingID;
    }

    try {
      const authToken = localStorage.getItem("accesstoken");

      const response = await fetch(`${API_URL}get-meeting-ID`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          accessToken: authToken,
          projectID,
          expertID,
        }),
      });

      const responseData = await response.json();

      if (responseData.status === "success") {
        const resolvedMeetingID = responseData.meetingID || null;
        setMeetingID(resolvedMeetingID);
        return resolvedMeetingID;
      }

      return null;
    } catch (error) {
      console.error("Error while fetching meeting ID:", error);
      return null;
    }
  };

  const generateRecordingLink = async (resolvedMeetingID) => {
    if (!resolvedMeetingID) {
      setErrorMessage("Meeting ID is missing.");
      return;
    }

    setLoadingVideo(true);
    setErrorMessage("");

    try {
      const response = await fetch(`${API_URL}get-meeting-video-recording`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          meetingID: resolvedMeetingID,
        }),
      });

      const data = await response.json();

      if (data.status === "success") {
        setRecordingUrl(data.recordingUrl || "");
        clickingOnProfile(expertID, 'Client generated meeting video link.');

        if (!data.recordingUrl) {
          setErrorMessage("Recording data was returned, but no recording URL was found.");
        }
      } else {
        setRecordingUrl("");
        setErrorMessage(data.text || data.message || "Failed to generate recording link.");
      }
    } catch (error) {
      console.error("Error while generating recording link:", error);
      setRecordingUrl("");
      setErrorMessage("Error while generating recording link.");
    } finally {
      setLoadingVideo(false);
    }
  };

  const loadMeetingVideo = async () => {
    setLoading(true);
    setErrorMessage("");
    setRecordingUrl("");

    try {
      const resolvedMeetingID = await getMeetingID();

      if (!resolvedMeetingID) {
        setErrorMessage("Meeting ID is missing.");
        return;
      }

      await generateRecordingLink(resolvedMeetingID);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMeetingVideo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectID, expertID, providedMeetingID]);

  const isBusy = loading || loadingVideo;

  return (
    <>
      <motion.div
        className="fixed inset-0 z-40 bg-slate-900/30 backdrop-blur-sm no-print"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      />

      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 no-print">
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 24, scale: 0.98 }}
          transition={{ duration: 0.22, ease: "easeOut" }}
          className="flex h-[calc(100vh-100px)] w-full max-w-6xl flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_24px_70px_rgba(15,23,42,0.22)]"
        >
          <div className="flex items-center justify-between gap-4 border-b border-slate-200 px-5 py-3">
            <div className="min-w-0">
              <p className="truncate text-lg font-semibold text-[#365561]">
                Meeting video
              </p>
              <p className="mt-1 text-xs text-slate-500">
                Securely stream or download the full meeting recording.
              </p>
              {meetingID ? (
                <p className="mt-1 text-[11px] text-slate-400">
                  Meeting ID: {meetingID}
                </p>
              ) : null}
            </div>

            <div className="flex items-center gap-2">
              {videoStreamUrl ? (
                <button
                  type="button"
                  onClick={() => {window.open(videoStreamUrl, "_blank"); clickingOnProfile(expertID, 'Client downloaded meeting video.');}}
                  className="inline-flex items-center gap-2 rounded-xl bg-[#365561] px-3 py-2 text-xs font-semibold text-white transition hover:bg-[#2c444d] focus:outline-none"
                >
                  <Download className="h-3.5 w-3.5" />
                  Download
                </button>
              ) : null}

              <button
                type="button"
                onClick={closeModal}
                className="rounded-2xl border border-slate-200 bg-white p-2 text-slate-500 transition hover:bg-slate-50 focus:outline-none"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto p-5">
            {isBusy ? (
              <div className="flex h-full min-h-[360px] flex-col items-center justify-center gap-3">
                <Loader2 className="h-8 w-8 animate-spin text-[#365561]" />
                <p className="text-sm font-semibold text-slate-500">
                  Generating secure video link...
                </p>
              </div>
            ) : videoStreamUrl ? (
              <AnimatePresence mode="wait">
                <motion.div
                  key="video-player"
                  initial={{ opacity: 0, x: 36 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -36 }}
                  transition={{ duration: 0.22, ease: "easeOut" }}
                  className="space-y-3"
                >
                  <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-950 shadow-sm">
                    <video
                      controls
                      preload="metadata"
                      className="aspect-video w-full bg-black"
                      src={videoStreamUrl}
                    >
                      Your browser does not support video playback.
                    </video>
                  </div>

                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => window.open(videoStreamUrl, "_blank")}
                      className="inline-flex items-center gap-2 rounded-xl bg-[#365561] px-3 py-2 text-xs font-semibold text-white transition hover:bg-[#2c444d] focus:outline-none"
                    >
                      <Download className="h-3.5 w-3.5" />
                      Download
                    </button>
                  </div>
                </motion.div>
              </AnimatePresence>
            ) : (
              <div className="flex h-full min-h-[360px] items-center justify-center">
                <div className="w-full max-w-xl rounded-3xl border border-dashed border-slate-200 bg-slate-50 px-5 py-10 text-center">
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-[#365561] shadow-sm">
                    <FileVideo className="h-7 w-7" />
                  </div>

                  <p className="mt-4 text-sm font-semibold text-slate-700">
                    No recording link generated yet.
                  </p>

                  {errorMessage ? (
                    <p className="mt-2 text-sm text-slate-500">
                      {errorMessage}
                    </p>
                  ) : (
                    <p className="mt-2 text-sm text-slate-500">
                      The recording may not be available yet, or this meeting was not recorded.
                    </p>
                  )}

                  <button
                    type="button"
                    onClick={loadMeetingVideo}
                    disabled={isBusy}
                    className="mt-4 inline-flex items-center gap-2 rounded-xl bg-[#365561] px-4 py-2 text-xs font-semibold text-white transition hover:bg-[#2c444d] disabled:cursor-wait disabled:opacity-60 focus:outline-none"
                  >
                    {isBusy ? (
                      <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Video className="h-3.5 w-3.5" />
                    )}
                    {isBusy ? "Generating..." : "Generate video link"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </>
  );
}
